import {
  getAllEntertainmentSubcategoryKeywords,
  getEntertainmentSubcategoryPriority,
  getLocaleAwareSubcategoryKeywords,
} from './languages';
import {
  type SubcategoryConfig,
  type SubcategoryResult,
  detectSubcategory,
} from './subcategoryDetection';
import { EXPENSE_TAXONOMY } from './taxonomy';

export const ENTERTAINMENT_KEYWORDS = getAllEntertainmentSubcategoryKeywords();

/** Derived from the canonical `EXPENSE_TAXONOMY`. See taxonomy.ts. */
export const VALID_ENTERTAINMENT_SUBCATEGORIES =
  EXPENSE_TAXONOMY.entertainment as readonly string[] as string[];

export const ENTERTAINMENT_CONFIG: SubcategoryConfig = {
  keywords: ENTERTAINMENT_KEYWORDS,
  validSubcategories: VALID_ENTERTAINMENT_SUBCATEGORIES,
  contextKeywords: {},
  minConfidenceThreshold: 0.3,
  subcategoryPriority: getEntertainmentSubcategoryPriority(),
};

export function detectEntertainmentSubcategory(description: string): string {
  return detectSubcategory(description, ENTERTAINMENT_CONFIG).subcategory;
}

export function detectEntertainmentSubcategoryWithConfidence(
  description: string
): SubcategoryResult {
  return detectSubcategory(description, ENTERTAINMENT_CONFIG);
}

/**
 * Locale-aware entertainment subcategory detector. Combines global
 * brand keywords (Netflix, Spotify, PlayStation, IMAX, Cinépolis, etc.)
 * with the user's primary-language common-word vocabulary. Returns
 * `default@0` when the locale doesn't resolve so cloud LLM can step in.
 *
 * Big win for entertainment: streaming brands (Netflix/Disney+/Crave/
 * Globoplay) work universally without each language file carrying its
 * own copy. Audiobook-concept words (audible/hörbuch/audiolivro) all
 * resolve to books regardless of source language.
 */
export function detectEntertainmentSubcategoryWithLocale(
  description: string,
  locale: string | undefined | null
): SubcategoryResult {
  const keywords = getLocaleAwareSubcategoryKeywords(
    b => b.entertainmentSubcategories,
    k => k.entertainmentSubcategories,
    locale
  );
  if (!keywords) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }
  return detectSubcategory(description, {
    ...ENTERTAINMENT_CONFIG,
    keywords,
  });
}
